import { StateType as SessionManagerStateType } from '@central-products/interface-session-manager';
import { feature as liveChatFeature } from '@central-products/live-chat-flow';
import { LiveChatWindowStateType } from '@central-products/interface-live-chat-flow';

import { isNative, isIos, isAndroid } from '../../utils';
import { ACCOUNT_ACTION_IDS } from '@central-products/interface-account-actions';
import { subscribe } from '@wh/message-bus';

function waitForVisitorId(maxRetries) {
  const oldVal = window.utag && window.utag.dle_visitor_id;
  return new Promise((resolve) => {
    let count = 0;
    const int = setInterval(() => {
      const newVal = window.utag && window.utag.dle_visitor_id;
      if (newVal !== oldVal || count >= maxRetries) {
        clearInterval(int);
        resolve(newVal);
        return;
      }
      count++;
    }, 500);
  });
}

function syncAudiencesViaVisitorService(bootstrapperFeatures, params) {
  const CALLBACK_NAME = 'liveChatPluginCb';
  window.kraken[CALLBACK_NAME] = function liveChatPluginCb(data) {
    if (!data.audiences) {
      return;
    }
    const audiences = Object.values(data.audiences);
    bootstrapperFeatures['live-chat-flow'].setAudiences(audiences);
  };

  bootstrapperFeatures['account-info'].onStateChanged(async ({ state }) => {
    if (state !== 'ACCOUNT_INFO_EXISTS') {
      return;
    }

    const visitorId = await waitForVisitorId(params.visitorIdMaxRetries);

    await fetch(params.tealiumInitUrl.replace('$(visitorId)', visitorId));

    const script = document.createElement('script');
    script.onload = function onload() {
      script.parentNode.removeChild(script);
    };
    script.src =
      params.visitorServiceUrl.replace('$(visitorId)', visitorId) +
      `?callback=window.kraken.${CALLBACK_NAME}&rnd=${new Date().getTime()}`;
    document.body.appendChild(script);
  });
}

const getEngagementName = async (bootstrapperFeatures, params) => {
  const engagement = { name: params.defaultEngagementNameUnAuth };

  try {
    const { state } = await bootstrapperFeatures['session-manager'].getState();
    if (state === SessionManagerStateType.LOGGED_IN) {
      engagement.name = params.defaultEngagementNameAuth;
    }
  } catch {}

  return engagement;
};

const shouldFallbackNativeIos = (allow) => isNative() && isIos() && allow;
const shouldFallbackNativeAndroid = (allow) => isNative() && isAndroid() && allow;

const switchNativeComponents = (action) => {
  if (
    (window?.ActionWebInterface?.supportedActions() || []).includes(action) &&
    typeof window.ActionWebInterface?.doAction === 'function'
  ) {
    window.ActionWebInterface.doAction(action, JSON.stringify(['carousel', 'stories']));
  }
};
const subscribeToLiveChatStateChange = (bootstrapperFeatures) => {
  bootstrapperFeatures['live-chat-flow'].onStateChanged(async ({ windowState }) => {
    if ([LiveChatWindowStateType.CLOSED, LiveChatWindowStateType.MINIMISED].includes(windowState)) {
      switchNativeComponents('ShowNativeComponent');
    }
    if (windowState === LiveChatWindowStateType.OPEN) {
      switchNativeComponents('HideNativeComponent');
    }
  });
};

export const subscribeToNativeFallback = (params) => {
  const allowIosNative = params.allowIosNative ?? false;
  const allowAndroidNative = params.allowAndroidNative ?? false;

  if (
    (shouldFallbackNativeIos(!allowIosNative) || shouldFallbackNativeAndroid(!allowAndroidNative)) &&
    params.nativeFallbackUrl
  ) {
    subscribe('live-person.open', () => {
      window.location.href = params.nativeFallbackUrl;
    });

    subscribe('wh.myAccount.event.footer.request.livePerson', () => {
      window.location.href = params.nativeFallbackUrl;
    });
  }
};

export const liveChatPlugin = {
  beforeBootstrap({ addBootstrapperFeature, params }) {
    const allowIosNative = params.allowIosNative ?? false;
    const allowAndroidNative = params.allowAndroidNative ?? false;

    if (shouldFallbackNativeIos(!allowIosNative) || shouldFallbackNativeAndroid(!allowAndroidNative)) {
      return;
    }

    addBootstrapperFeature(liveChatFeature, params);
  },
  postBootstrap({ bootstrapperFeatures, params }) {
    if (!bootstrapperFeatures['live-chat-flow']) {
      subscribeToNativeFallback(params);
      return;
    }

    if (params.syncAudiencesViaVisitorService) {
      syncAudiencesViaVisitorService(bootstrapperFeatures, params);
    }

    const triggerChat = bootstrapperFeatures['live-chat-flow'].activateEngagement;
    const errorFallback = (response) => {
      if (response instanceof Error && params.fallbackUrl) {
        window.open(params.fallbackUrl);
      }
    };

    if (isAndroid()) {
      subscribeToLiveChatStateChange(bootstrapperFeatures);
    }

    bootstrapperFeatures['account-actions'].onActionRequested(({ actionId, data }) => {
      if (actionId === ACCOUNT_ACTION_IDS.LIVE_CHAT_START) {
        triggerChat(data).then(errorFallback).catch(errorFallback);
      }
    });

    window.addEventListener('message', (event) => {
      if (event.data.type === 'openCashierLiveChatSB') {
        const engagementName = event.data.name || params.defaultEngagementNameAuth;
        triggerChat({ name: engagementName }).then(errorFallback).catch(errorFallback);
      }
    });

    if (params.defaultEngagementNameUnAuth && params.defaultEngagementNameAuth) {
      subscribe('live-person.open', async () => {
        triggerChat(await getEngagementName(bootstrapperFeatures, params))
          .then(errorFallback)
          .catch(errorFallback);
      });

      subscribe('wh.myAccount.event.footer.request.livePerson', async () => {
        triggerChat(await getEngagementName(bootstrapperFeatures, params))
          .then(errorFallback)
          .catch(errorFallback);
      });
    }
  },
};
